import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthAxios, useAuth } from '@artprov/design-system';
import { parseARtoISO } from '../../../common/utils/functions/dateUtils';
import { getPlural } from '../../../common/utils/functions/textUtils';

/**
 * Hook personalizado para manejar las acciones masivas (Confirmar, Descartar)
 * Centraliza la lógica de llamadas al backend y manejo de respuestas
 */
export function useMatchingBancarioActions(uiActions) {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const {
        closeModal,
        resetSelection,
        setProcessing,
        setResultData,
        openModal,
        showAlert,
    } = uiActions;

    /**
     * Invalida las queries para refrescar los datos
     */
    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['matching-bancario'] });
        queryClient.invalidateQueries({ queryKey: ['matching-estadisticas'] });
    }, [queryClient]);

    /**
     * Handler para confirmar registros
     */
    const handleConfirmAccept = useCallback(
        async (rows, fechaContable) => {
            try {
                closeModal('confirm');
                setProcessing(true);

                const response = await getAuthAxios('default').post(
                    `/matching-bancario/confirmar`,
                    {
                        records: rows.map((row) => ({
                            id: row.ID_EMAIL,
                            codigoEstado: row.EB_ESTADO_COD,
                            fechaUltimoCierre: parseARtoISO(row.ULT_CIERRE_CONTABLE),
                        })),
                        fechaContable,
                        user: user.preferred_username.toUpperCase(),
                    },
                    {
                        timeout: 300000,
                    }
                );

                // Mostrar modal con los resultados exitosos
                setResultData(response.data);
                resetSelection();
                openModal('result');
            } catch (error) {
                // Manejar error con alerta
                const errorResponse = error.response?.data;
                const errorMessage =
                    errorResponse?.message || error.message || 'Error desconocido';
                const statusCode = error.response?.status || error.status;

                showAlert({
                    variant: 'error',
                    title:
                        statusCode === 400
                            ? `Error al confirmar ${getPlural(rows.length, 'embargo', 'embargos')}`
                            : '',
                    description:
                        statusCode === 400
                            ? errorMessage
                            : `Hubo un error al confirmar ${getPlural(rows.length, 'el embargo', 'los embargos')}. Intentá nuevamente más tarde.`,
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            setResultData,
            openModal,
            showAlert,
            user,
        ]
    );

    /**
     * Handler para descartar registros
     */
    const handleDescartarAccept = useCallback(
        async (rows) => {
            try {
                closeModal('descartar');
                setProcessing(true);

                await getAuthAxios('default').post(`/matching-bancario/descartar`, {
                    records: rows.map((row) => ({
                        id: row.ID_EMAIL,
                        codigoEstado: row.EB_ESTADO_COD
                    })),
                    user: user.preferred_username.toUpperCase(),
                });

                // Éxito - mostrar alerta
                showAlert({
                    variant: 'success',
                    title: `${getPlural(rows.length, 'Embargo', 'Embargos')} ${getPlural(rows.length, 'descartado', 'descartados')} exitosamente`,
                    containerStyle: 'h-[68px]',
                });

                // Refrescar datos
                refreshData();
                resetSelection();
            } catch (error) {
                // Error - mostrar alerta de error
                const errorResponse = error.response?.data;
                const errorMessage =
                    errorResponse?.message || error.message || 'Error desconocido';
                const statusCode = error.response?.status || error.status;

                showAlert({
                    variant: 'error',
                    title:
                        statusCode === 400
                            ? `Error al descartar ${getPlural(rows.length, 'embargo', 'embargos')}`
                            : '',
                    description:
                        statusCode === 400
                            ? errorMessage
                            : `Hubo un error al descartar ${getPlural(rows.length, 'el embargo', 'los embargos')}. Intentá nuevamente más tarde.`,
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            showAlert,
            refreshData,
            user,
        ]
    );

    /**
     * Handler para reprocesar registros
     */
    const handleReprocesarAccept = useCallback(
        async (rows) => {
            try {
                closeModal('reprocesar');
                setProcessing(true);

                await getAuthAxios('default').post(`/matching-bancario/reprocesar`, {
                    records: rows.map((row) => ({
                        id: row.ID_EMAIL,
                        codigoEstado: row.EB_ESTADO_COD,
                    })),
                    user: user.preferred_username.toUpperCase(),
                });

                // Éxito - mostrar alerta
                showAlert({
                    variant: 'success',
                    title: `${getPlural(rows.length, 'Embargo', 'Embargos')} ${getPlural(rows.length, 'reprocesado', 'reprocesados')} exitosamente`,
                    containerStyle: 'h-[68px]',
                });

                // Refrescar datos
                refreshData();
                resetSelection();
            } catch (error) {
                // Error - mostrar alerta de error
                const errorResponse = error.response?.data;
                const errorMessage =
                    errorResponse?.message || error.message || 'Error desconocido';
                const statusCode = error.response?.status || error.status;

                showAlert({
                    variant: 'error',
                    title:
                        statusCode === 400
                            ? `Error al reprocesar ${getPlural(rows.length, 'embargo', 'embargos')}`
                            : '',
                    description:
                        statusCode === 400
                            ? errorMessage
                            : `Hubo un error al reprocesar ${getPlural(rows.length, 'el embargo', 'los embargos')}. Intentá nuevamente más tarde.`,
                });
            } finally {
                setProcessing(false);
            }
        },
        [
            closeModal,
            resetSelection,
            setProcessing,
            showAlert,
            refreshData,
            user,
        ]
    );

    /**
     * Handler para cerrar el modal de resultados
     */
    const handleCloseResultModal = useCallback(() => {
        closeModal('result');
        setResultData(null);
        refreshData();
    }, [closeModal, setResultData, refreshData]);

    return {
        handleConfirmAccept,
        handleDescartarAccept,
        handleReprocesarAccept,
        handleCloseResultModal,
    };
}
